import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Snackbar, Alert, useTheme } from "@mui/material";
import { Item } from "./Item";
import api from "services/api";

const SERVICE_TAG_REGEX = /#(\d+)/g;

/**
 * NoteCard component - displays a note with clickable service tags
 * Service tags are formatted as #ProjectNumber (e.g., #1201524)
 * Click to navigate to the service profile
 */
export const NoteCard = ({
  title,
  items,
  justify = "start",
  padding = null,
  onTagClick = null,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  /**
   * Maps service category to route path
   */
  const getServiceRoutePath = (service) => {
    if (!service?.id) return null;
    
    if (service.ServiceCategory === "Translation") return `/translations/${service.id}`;
    if (service.ServiceCategory === "Interpretation") return `/interpretations/${service.id}`;
    return `/otherServices/${service.id}`;
  };

  /**
   * Handles clicking a service tag
   */
  const handleServiceTagClick = async (e, projectNumber) => {
    e.stopPropagation();

    try {
      const response = await api.get(
        `/services/tag-search?query=${encodeURIComponent(projectNumber)}&limit=10`
      );

      const data = response.data;

      if (!data || data.length === 0) {
        setSnackbar({
          open: true,
          message: `Service #${projectNumber} not found`,
          severity: "warning",
        });
        return;
      }

      // Find exact match or use first result
      const service =
        data.find((s) => s.ProjectNumber === projectNumber) ||
        data[0];

      const routePath = getServiceRoutePath(service);

      if (!routePath) {
        setSnackbar({
          open: true,
          message: "Could not resolve service category",
          severity: "error",
        });
        return;
      }

      // Trigger optional callback before navigating
      if (onTagClick) {
        onTagClick(service);
      }

      navigate(routePath);
    } catch (error) {
      console.error("Error clicking service tag:", error);
      setSnackbar({
        open: true,
        message: "Error navigating to service",
        severity: "error",
      });
    }
  };

  /**
   * Renders the note title with clickable service tags
   */
  const renderNoteWithServiceTags = (text) => {
    if (!text) return null;

    const parts = [];
    let lastIndex = 0;
    let match;

    // Reset regex lastIndex for reuse
    SERVICE_TAG_REGEX.lastIndex = 0;

    while ((match = SERVICE_TAG_REGEX.exec(text)) !== null) {
      // Add text before the tag
      if (match.index > lastIndex) {
        parts.push(
          <span key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</span>,
        );
      }

      const projectNumber = match[1];

      // Add clickable tag
      parts.push(
        <span
          key={`tag-${match.index}`}
          onClick={(e) => handleServiceTagClick(e, projectNumber)}
          style={{
            color: theme.palette.primary.main,
            cursor: "pointer",
            textDecoration: "underline",
            fontWeight: 500,
            transition: "opacity 0.2s ease",
          }}
          onMouseEnter={(e) => (e.target.style.opacity = "0.8")}
          onMouseLeave={(e) => (e.target.style.opacity = "1")}
        >
          #{projectNumber}
        </span>,
      );

      lastIndex = SERVICE_TAG_REGEX.lastIndex;
    }

    // Add remaining text
    if (lastIndex < text.length) {
      parts.push(
        <span key={`text-${lastIndex}`}>{text.slice(lastIndex)}</span>,
      );
    }

    return parts.length > 0 ? parts : text;
  };

  const justifyContent = justify === "center" ? "center" : "left"; // Handle justify prop
  const subtitleAlignment = "flex-start";
  const subtitlePadding = justify === "start" ? "0 0.25rem" : "0";
  const itemPadding =
    padding === null || padding === undefined
      ? theme.spacing(2)
      : `${theme.spacing(2)} ${theme.spacing(padding)}`;

  const titleColor = theme.palette.primary.text;
  const titleWeight = typeof items === "string" ? "normal" : "bold";

  // Convert items to an array
  const itemsArray = Array.isArray(items)
    ? items
    : typeof items === "string"
      ? [{ subtitle: "", content: items }]
      : [];

  // Display items (pad to 3 entries like Card2)
  const displayedItems = itemsArray.length > 0 
    ? [
        itemsArray[0] || { subtitle: "", content: "" },
        itemsArray[1] || { subtitle: "", content: "" },
        itemsArray[2] || { subtitle: "", content: "" },
      ].slice(0, itemsArray.length)
    : [];

  return (
    <>
      <Item
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: justifyContent, // Left-aligned notes
          padding: itemPadding,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems={justifyContent} // Left-aligned
          width="100%"
          gap={0.5}
        >
          {/* Note title with clickable tags */}
          <Typography
            variant="body1"
            style={{
              color: titleColor,
              fontWeight: titleWeight,
              marginBottom: theme.spacing(1),
              textAlign: justifyContent,
              wordBreak: "break-word",
              whiteSpace: "pre-wrap",  // Preserve newlines and wrap text
              lineHeight: "normal",    // Ensure proper line rendering for multiline content
            }}
          >
            {renderNoteWithServiceTags(title)}
          </Typography>

          {/* Note metadata (member, date) */}
          <Box display="flex" flexDirection="column" width="100%" gap={1}>
            {displayedItems.map((item, index) => (
              <Box key={index}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems={justifyContent}
                  width="100%"
                >
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: "lighter",
                      alignSelf: subtitleAlignment,
                      padding: subtitlePadding,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "35%",
                    }}
                  >
                    {item.subtitle}
                  </Typography>
                  <Typography
                    variant="body1"
                    style={{
                      fontWeight: "lighter",
                      alignSelf: subtitleAlignment,
                      padding: subtitlePadding,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {item.content}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Item>

      {/* Snackbar for error messages */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
